import React, { useEffect, useState, useCallback, useMemo } from 'react';
import articles from './ArticlesData';
import '../styles/SesConferences.css';
import '../styles/Root.css';

const SesConferences = () => {
    const [upcomingConferences, setUpcomingConferences] = useState([]);
    const [archivedConferences, setArchivedConferences] = useState([]);
    const [expandedYear, setExpandedYear] = useState(null);
    const [pdfViewerVisible, setPdfViewerVisible] = useState(false);
    const [selectedPdfPath, setSelectedPdfPath] = useState(null);
    const [isMobile, setIsMobile] = useState(false);

    const parseDate = useCallback((dateStr) => {
        const [day, month, year] = dateStr.split(' ');
        const monthNames = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
        const monthIndex = monthNames.indexOf(month.toLowerCase());
        return new Date(year, monthIndex, day);
    }, []);

    useEffect(() => {
        const conferenceArticles = articles.filter(article => article.conference === true);
        const sortedConferences = [...conferenceArticles].sort((a, b) => parseDate(b.date) - parseDate(a.date));

        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        const upcoming = sortedConferences.filter(conf => parseDate(conf.date) >= currentDate).reverse();
        const archived = sortedConferences.filter(conf => parseDate(conf.date) < currentDate);

        setUpcomingConferences(upcoming);
        setArchivedConferences(archived);

        const checkIsMobile = () => setIsMobile(window.innerWidth <= 768);
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return () => window.removeEventListener('resize', checkIsMobile);
    }, [parseDate]);

    // Groupement des archives par année
    const groupedArchives = useMemo(() => {
        const groups = archivedConferences.reduce((acc, conference) => {
            const year = parseDate(conference.date).getFullYear();
            if (!acc[year]) acc[year] = [];
            acc[year].push(conference);
            return acc;
        }, {});
        return Object.entries(groups).sort((a, b) => b[0] - a[0]);
    }, [archivedConferences, parseDate]);

    const scrollGallery = (direction, elementId) => {
        const container = document.getElementById(elementId);
        if (container) {
            // On calcule la largeur d'un item + le gap dynamiquement
            const item = container.querySelector('.news-item');
            const gap = parseInt(window.getComputedStyle(container).gap) || 24;
            const scrollAmount = item ? (item.offsetWidth + gap) : 324;
            
            container.scrollBy({
                left: direction * scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const toggleAccordion = (year) => {
        setExpandedYear(prevYear => (prevYear === year ? null : year));
    };

    const openPdfViewer = (pdfPath) => {
        if (isMobile) {
            window.open(pdfPath, '_blank');
        } else {
            setSelectedPdfPath(pdfPath);
            setPdfViewerVisible(true);
            document.body.style.overflow = 'hidden';
        }
    };

    const closePdfViewer = () => {
        setPdfViewerVisible(false);
        setSelectedPdfPath(null);
        document.body.style.overflow = 'unset';
    };

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && pdfViewerVisible) closePdfViewer();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [pdfViewerVisible]);

    const downloadPdf = () => {
        if (selectedPdfPath) {
            const link = document.createElement('a');
            link.href = selectedPdfPath;
            link.download = selectedPdfPath.split('/').pop();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <main className="ses-conferences-page">
            <section className="hero fade-in">
                <h1>Ses Conférences</h1>
                <p>Découvrez les conférences organisées par l'Association des Amis du Musée</p>
            </section>

            <section className="content-section fade-in">
                <h2>Introduction</h2>
                <p>L'Association des Amis du Musée organise régulièrement des conférences sur divers thèmes liés à l'histoire militaire et coloniale.</p>
            </section>

            <section className="news-section fade-in">
                <h2>Les conférences au programme</h2>
                <div className="news-grid-container">
                    <button className="arrow left-arrow" onClick={() => scrollGallery(-1, 'upcoming-grid')} aria-label="Précédent">&#10094;</button>
                    <div className="news-grid" id="upcoming-grid">
                        {upcomingConferences.length > 0 ? (
                            upcomingConferences.map(conference => (
                                <div key={conference.id} className="news-item">
                                    <img src={conference.image} alt={conference.title} />
                                    <div className="news-item-content">
                                        <h3>{conference.title}</h3>
                                        <p className="date">{conference.date}</p>
                                        <p className="description">{conference.description}</p>
                                        <button className="btn btn-primary" onClick={() => openPdfViewer(conference.pdfPath)}>Lire l'article</button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="no-conferences-message">Aucune conférence à venir pour le moment.</p>
                        )}
                    </div>
                    <button className="arrow right-arrow" onClick={() => scrollGallery(1, 'upcoming-grid')} aria-label="Suivant">&#10095;</button>
                </div>
            </section>

            <section className="content-section fade-in">
                <h2>Les conférences archivées</h2>
                <div className="archived-container">
                    {groupedArchives.map(([year, conferences]) => (
                        <div key={year} className="accordion-wrapper">
                            <div 
                                className={`accordion-header ${expandedYear === parseInt(year) ? 'active' : ''}`}
                                onClick={() => toggleAccordion(parseInt(year))}
                            >
                                <h3>{year}</h3>
                                <span className="accordion-icon">{expandedYear === parseInt(year) ? '−' : '+'}</span>
                            </div>
                            {expandedYear === parseInt(year) && (
                                <div className="accordion-content show">
                                    <div className="accordion-grid-wrapper">
                                        <button className="arrow left-arrow" onClick={() => scrollGallery(-1, `grid-${year}`)}>&#10094;</button>
                                        <div className="news-grid" id={`grid-${year}`}>
                                            {conferences.map(conf => (
                                                <div key={conf.id} className="news-item">
                                                    <img src={conf.image} alt={conf.title} />
                                                    <div className="news-item-content">
                                                        <h3>{conf.title}</h3>
                                                        <p className="date">{conf.date}</p>
                                                        <p className="description">{conf.description}</p>
                                                        <button className="btn btn-primary" onClick={() => openPdfViewer(conf.pdfPath)}>Lire l'article</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <button className="arrow right-arrow" onClick={() => scrollGallery(1, `grid-${year}`)}>&#10095;</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {pdfViewerVisible && selectedPdfPath && (
                <div className="pdf-viewer-overlay" onClick={closePdfViewer}>
                    <div className="pdf-viewer-container" onClick={(e) => e.stopPropagation()}>
                        <div className="pdf-viewer-header">
                            <button className="pdf-download-btn" onClick={downloadPdf}>Télécharger</button>
                            <button className="close-pdf" onClick={closePdfViewer}>&times;</button>
                        </div>
                        <div className="pdf-content">
                            <iframe src={selectedPdfPath} className="pdf-viewer" title="PDF"></iframe>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
};

export default SesConferences;